import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';

import CreatePasswordForm from '../components/forms/CreatePasswordForm';
import LoginLayout from '../components/layout/LoginLayout';
import { completeNewPassword, createPassword } from '../services/api/implementation/impl';

const CreatePasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const email = decodeURIComponent(queryParams.get('email') || '');
  const code = queryParams.get('code');
  const session = queryParams.get('session');
  const isChallengeFlow = location.pathname === '/auth/complete-new-password';

  const { mutate: createPasswordMutation, isLoading } = useMutation({
    mutationFn: async (payload) =>
      isChallengeFlow ? await completeNewPassword(payload) : await createPassword(payload),
    onSuccess: async () => {
      toast.success('Password created successfully');
      navigate('/auth/login', { replace: true });
    },
    onError: (err) => {
      console.error('Error creating password');
      toast.error(err?.response?.data?.message || err?.message || 'Error creating password');
    }
  });

  const handleCreatePassword = (data) => {
    if (!email) {
      toast.error('Email is missing. Please try again from login.');
      return;
    }

    if (isChallengeFlow && !session) {
      toast.error('Session is missing. Please login again.');
      navigate('/auth/login');
      return;
    }

    if (!isChallengeFlow && !code) {
      toast.error('Reset code is missing. Please try from the email link again.');
      return;
    }

    const payload = isChallengeFlow
      ? {
          email,
          newPassword: data.password,
          session
        }
      : {
          newPassword: data.password,
          email,
          code
        };

    let message = 'Password must contain: ';
    if (!/(?=.*[a-z])/.test(data.password)) message += '\n- At least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(data.password)) message += '\n- At least one uppercase letter';
    if (!/(?=.*\d)/.test(data.password)) message += '\n- At least one digit';
    if (!/(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(data.password))
      message += '\n- At least one special character';
    if (data.password.length < 8) message += '\n- Minimum length of 8 characters';

    if (message !== 'Password must contain: ') {
      toast.error(message);
      return;
    }

    createPasswordMutation(payload);
  };

  return (
    <LoginLayout>
      <CreatePasswordForm
        onSubmit={handleCreatePassword}
        isLoading={isLoading}
        isChallengeFlow={isChallengeFlow}
      />
    </LoginLayout>
  );
};

export default CreatePasswordPage;
